// The direct-versus-Airbnb comparison, computed once and stated everywhere.
//
// Four surfaces say what booking direct saves: the quote card on the stay
// page, the FAQ (visible page, FAQPage JSON-LD, prerender), the /api/stay
// facts endpoint, and the MCP price_quote tool. Before 2026-09-20 the card
// showed only the 5% rent line, and the real gap (5% off rent, a lower
// cleaning fee, and no guest service fee) was explained in the abstract in
// the FAQ. This module is the one calculation all of them read, so the
// number on the card and the sentence in the FAQ cannot disagree.
//
// WHAT IS KNOWN AND WHAT IS ESTIMATED (pricing architecture, DECISIONS.md
// 2026-07-29 and 2026-08-01):
//   - The OwnerRez nightly rate is the Airbnb nightly rate (PriceLabs engine
//     price x 1.1834, pushed to every channel). Direct is that rate minus
//     DIRECT_DISCOUNT_PCT (3% since 2026-09-20, 5% before), applied in the
//     quote as a discount line. So the quote's undiscounted
//     rent IS the Airbnb rent for the same dates. Known, not estimated.
//   - Cleaning: property-config carries both figures (cleaningFee direct,
//     cleaningFeeAirbnb on Airbnb), mirroring the two OwnerRez surcharges.
//     Known.
//   - Airbnb's guest service fee is Airbnb's, not ours, and it varies by
//     booking. Airbnb's help article "Airbnb service fees"
//     (airbnb.com/help/article/1857, read 2026-09-20) says guests pay 14.1%
//     to 16.5% of the booking subtotal, where the subtotal is the nightly
//     price plus any host fees (cleaning), excluding the service fee and
//     taxes. We use the LOW end so the estimate can only understate the gap.
//     Estimated, and every surface labels it so.
//   - Taxes are the same rate on both channels (13% on rent + cleaning for
//     this property; Airbnb collects its own on Airbnb bookings, and applies
//     the rate to its service fee too, read off the tax lines of real Airbnb
//     bookings 2026-07-27). The comparison is made WITH TAX on both sides
//     (owner call 2026-09-20, so the direct figure equals the card's Total):
//     the direct taxes are the quote's own lines, and the Airbnb taxes apply
//     the quote's effective rate to the whole Airbnb subtotal, service fee
//     included. Before-tax figures travel along for anyone who wants them.
//
// INTEGRITY RULE: never call the Airbnb figure a price. It is an estimate
// built from the assumptions above, and the assumption line travels with the
// number wherever it is shown.

import { DIRECT_DISCOUNT_PCT } from './property-schema.js';

// Airbnb guest service fee, as a percent of the booking subtotal (nightly
// price + cleaning, before taxes). Low end of Airbnb's published range.
// Recheck against the help article when it drifts; this is the ONLY place
// the number lives.
export const AIRBNB_GUEST_FEE_PCT = 14.1;
export const AIRBNB_GUEST_FEE_RANGE = '14.1% to 16.5%';
export const AIRBNB_GUEST_FEE_SOURCE = 'https://www.airbnb.com/help/article/1857';
export const AIRBNB_GUEST_FEE_CHECKED = '2026-09-20';

function money(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

// Classify an OwnerRez quote's lines into the buckets the comparison needs.
// Handles the v2 `charges` collection (each { description, amount, type })
// and the older rent/surcharges/taxes shape. Returns null when no rent line
// can be found: a comparison built without a rent figure would be a guess.
export function quoteBreakdown(quote) {
  if (!quote) return null;
  let lines;
  if (Array.isArray(quote.charges) && quote.charges.length) {
    lines = quote.charges.map((c) => ({
      label: String(c.description || c.type || ''),
      amount: Number(c.amount) || 0,
      type: String(c.type || '')
    }));
  } else {
    lines = [];
    const rentAmt = quote.rent ?? quote.subtotal;
    if (rentAmt != null) lines.push({ label: 'rent', amount: Number(rentAmt) || 0, type: 'rent' });
    (quote.surcharges || []).forEach((s) =>
      lines.push({ label: String(s.description || s.name || ''), amount: Number(s.amount) || 0, type: 'surcharge' })
    );
    (quote.taxes || []).forEach((t) =>
      lines.push({ label: String(t.description || t.name || ''), amount: Number(t.amount) || 0, type: 'tax' })
    );
  }

  let rent = 0;
  let nights = null;
  let discounts = 0;
  let cleaning = 0;
  let otherFees = 0;
  let taxes = 0;
  let foundRent = false;

  for (const l of lines) {
    if (/^tax$/i.test(l.type) || /\btax\b/i.test(l.label)) {
      taxes += l.amount;
      continue;
    }
    const nightsMatch = /^(\d+)\s+nights?\b/i.exec(l.label);
    if ((/^rent$/i.test(l.type) || nightsMatch) && l.amount > 0) {
      rent += l.amount;
      foundRent = true;
      if (nightsMatch && nights == null) nights = Number(nightsMatch[1]);
      continue;
    }
    if (l.amount < 0) {
      discounts += l.amount;
      continue;
    }
    if (/cleaning/i.test(l.label)) {
      cleaning += l.amount;
      continue;
    }
    otherFees += l.amount;
  }
  if (!foundRent) return null;

  if (nights == null && quote.arrival && quote.departure) {
    const ms = Date.parse(`${quote.departure}T00:00:00Z`) - Date.parse(`${quote.arrival}T00:00:00Z`);
    if (Number.isFinite(ms) && ms > 0) nights = Math.round(ms / 86400000);
  }

  const preTax = money(rent + discounts + cleaning + otherFees);
  return {
    nights,
    rent: money(rent),
    discounts: money(discounts),
    cleaning: money(cleaning),
    otherFees: money(otherFees),
    taxes: money(taxes),
    preTax,
    total: money(preTax + taxes)
  };
}

// The comparison itself, from a breakdown. `rent` is the undiscounted rent
// (what Airbnb charges for the same nights), `discounts` is negative.
// Returns null when there is nothing to compare.
export function compareWithAirbnb(breakdown, property) {
  if (!breakdown || !(breakdown.rent > 0)) return null;
  const airbnbCleaning =
    property?.cleaningFeeAirbnb != null ? Number(property.cleaningFeeAirbnb) : breakdown.cleaning;
  const directPreTax = breakdown.preTax;
  const airbnbSubtotal = money(breakdown.rent + airbnbCleaning + breakdown.otherFees);
  const airbnbGuestFee = money(airbnbSubtotal * (AIRBNB_GUEST_FEE_PCT / 100));
  const airbnbPreTax = money(airbnbSubtotal + airbnbGuestFee);

  // Effective tax rate from the quote itself (the five OwnerRez lines over
  // the taxable subtotal), so the Airbnb side is taxed exactly as the direct
  // side was, never from a hardcoded rate. A quote with no tax lines (not
  // expected for this property) compares both sides untaxed.
  const directTaxes = money(breakdown.taxes);
  const taxRate = directPreTax > 0 && directTaxes > 0 ? directTaxes / directPreTax : 0;
  const airbnbTaxes = money(airbnbPreTax * taxRate);
  const directTotal = money(directPreTax + directTaxes);
  const airbnbTotal = money(airbnbPreTax + airbnbTaxes);
  const saving = money(airbnbTotal - directTotal);
  const savingPct = airbnbTotal > 0 ? Math.round((saving / airbnbTotal) * 100) : 0;
  const taxRatePct = Math.round(taxRate * 1000) / 10;

  return {
    basis: 'with-tax',
    nights: breakdown.nights,
    taxRatePct,
    direct: {
      rent: money(breakdown.rent + breakdown.discounts),
      cleaning: breakdown.cleaning,
      otherFees: breakdown.otherFees,
      guestServiceFee: 0,
      preTax: directPreTax,
      taxes: directTaxes,
      total: directTotal
    },
    airbnb: {
      estimated: true,
      rent: breakdown.rent,
      cleaning: money(airbnbCleaning),
      otherFees: breakdown.otherFees,
      guestServiceFee: airbnbGuestFee,
      guestServiceFeePct: AIRBNB_GUEST_FEE_PCT,
      preTax: airbnbPreTax,
      taxes: airbnbTaxes,
      total: airbnbTotal
    },
    legs: {
      rent: money(-breakdown.discounts),
      cleaning: money(airbnbCleaning - breakdown.cleaning),
      guestServiceFee: airbnbGuestFee,
      taxes: money(airbnbTaxes - directTaxes)
    },
    saving,
    savingPct,
    assumption: airbnbAssumptionLine(property, taxRatePct)
  };
}

// The quote-shaped entry point: the server attaches this to every quote.
export function compareQuoteWithAirbnb(quote, property) {
  return compareWithAirbnb(quoteBreakdown(quote), property);
}

// One sentence that must accompany the Airbnb figure wherever it is shown.
// Plain statement of the assumptions, nothing else (plain-credibility rule).
export function airbnbAssumptionLine(property, taxRatePct) {
  const cleaning =
    property?.cleaningFeeAirbnb != null ? `Airbnb's $${Number(property.cleaningFeeAirbnb).toFixed(2)} cleaning fee` : 'the same cleaning fee';
  const tax =
    taxRatePct > 0
      ? `the same ${taxRatePct}% tax on both, which Airbnb applies to its service fee as well`
      : 'the same tax rate on both';
  return `Airbnb estimate: the same nightly rate before our ${DIRECT_DISCOUNT_PCT}% direct discount, ${cleaning}, Airbnb's guest service fee at ${AIRBNB_GUEST_FEE_PCT}% of the subtotal (the low end of the ${AIRBNB_GUEST_FEE_RANGE} Airbnb publishes), and ${tax}.`;
}

// A worked example for prose surfaces (the FAQ) that have no dates in hand.
// Built through the same function as the card so the two agree by
// construction. `airbnbRent` is the Airbnb nightly total for the example.
export function exampleComparison(property, airbnbRent = 1500) {
  const discount = -money(airbnbRent * (DIRECT_DISCOUNT_PCT / 100));
  const cleaning = property?.cleaningFee != null ? Number(property.cleaningFee) : 0;
  const preTax = money(airbnbRent + discount + cleaning);
  const taxes = money(preTax * ((Number(property?.taxRatePercent) || 0) / 100));
  return compareWithAirbnb(
    {
      nights: null,
      rent: money(airbnbRent),
      discounts: discount,
      cleaning,
      otherFees: 0,
      taxes,
      preTax,
      total: money(preTax + taxes)
    },
    property
  );
}
